import {
  Box,
  Button,
  Card,
  Container,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import Confetti from "react-confetti";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import { Brand, Collab, User } from "../../../gql/__generated__/graphql";
import { userInfoAtom } from "../../atoms";
import FullPageMessage from "../../components/FullPageMessage/FullPageMessage";
import ReviewModal from "../../components/Modals/ReviewModal/ReviewModal";
import PageHeader from "../../components/PageHeader/PageHeader";
import { confettiCollabsAtom, fundedCollabIdAtom } from "./atoms";
import classes from "./CollabsPage.module.css";
import CollabsList from "./components/CollabsList";
import { useCollabs } from "./useCollabs";

function CollabsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Jotai state
  const userInfo = useAtomValue(userInfoAtom);
  const confettiCollabs = useAtomValue(confettiCollabsAtom);
  const setConfettiCollabs = useSetAtom(confettiCollabsAtom);
  const setFundedCollabId = useSetAtom(fundedCollabIdAtom);

  // Modals state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [entityForReviewModal, setEntityForReviewModal] = useState<
    User | Brand | undefined
  >(undefined);
  const [fundingSuccessModalOpen, setFundingSuccessModalOpen] = useState(false);

  // Funded collab state
  const [fundingSuccessCollabId, setFundingSuccessCollabId] = useState<
    string | null
  >(null);
  const [fundedCollabDetails, setFundedCollabDetails] = useState<Collab | null>(
    null
  );

  // Effect to check URL params for successful funding
  useEffect(() => {
    const fundingStatus = searchParams.get("funding");
    const collabId = searchParams.get("id");

    if (fundingStatus === "success" && collabId) {
      setFundingSuccessCollabId(collabId);
      setFundingSuccessModalOpen(true);
      setConfettiCollabs(true);
      setFundedCollabId(collabId); // For card animation

      // Clean up URL by removing query parameters
      searchParams.delete("funding");
      searchParams.delete("id");
      setSearchParams(searchParams, { replace: true });

      // Stop confetti after a while
      setTimeout(() => {
        setConfettiCollabs(false);
        setFundedCollabId(null);
      }, 6000); // A bit longer for confetti
    }
  }, [searchParams, setSearchParams, setConfettiCollabs, setFundedCollabId]);

  const handleOpenReviewModal = (entity: User | Brand) => {
    setEntityForReviewModal(entity);
    setReviewModalOpen(true);
  };

  if (!userInfo) {
    return (
      <FullPageMessage
        title="Authentication Required"
        message="You must be logged in to view this page."
        type="warning"
        actionButton={{
          label: "Sign In",
          onClick: () => navigate("/login"),
        }}
      />
    );
  }

  // Collabs state
  const {
    pendingCollabs,
    acceptedCollabs,
    reviewingCollabs,
    historyCollabs,
    actionRequiredCounts,
    loading,
    error,
  } = useCollabs(userInfo.userType);

  const allCollabs = useMemo(
    () => [
      ...pendingCollabs,
      ...acceptedCollabs,
      ...reviewingCollabs,
      ...historyCollabs,
    ],
    [pendingCollabs, acceptedCollabs, reviewingCollabs, historyCollabs]
  );

  // Effect to find collab details once collabs are loaded
  useEffect(() => {
    if (fundingSuccessCollabId && allCollabs.length > 0) {
      const fundedCollab = allCollabs.find(
        (c) => c.id === fundingSuccessCollabId
      );
      if (fundedCollab) {
        setFundedCollabDetails(fundedCollab);
      }
    }
  }, [fundingSuccessCollabId, allCollabs]);

  // Handle loading and error states
  if (loading) return <LoadingOverlay visible={true} />;
  if (error)
    return (
      <FullPageMessage
        title="An error ocurred while fetching collabs."
        message="Please try again, or contact us if this persists."
        type="error"
      />
    );

  return (
    <Box data-component="CollabsPage">
      {confettiCollabs && <Confetti />}
      <PageHeader title="Collabs" subtitle="Manage your collabs." />

      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Tabs defaultValue="pending" variant="outline">
            <Tabs.List grow>
              <Tabs.Tab
                value="pending"
                rightSection={
                  actionRequiredCounts.pending > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.pending}
                    </Box>
                  ) : null
                }
              >
                Pending
              </Tabs.Tab>
              <Tabs.Tab
                value="accepted"
                rightSection={
                  actionRequiredCounts.accepted > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.accepted}
                    </Box>
                  ) : null
                }
              >
                Accepted
              </Tabs.Tab>
              <Tabs.Tab
                value="reviewing"
                rightSection={
                  actionRequiredCounts.reviewing > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.reviewing}
                    </Box>
                  ) : null
                }
              >
                Reviewing
              </Tabs.Tab>
              <Tabs.Tab
                value="history"
                rightSection={
                  actionRequiredCounts.history > 0 ? (
                    <Box className={classes.notificationBadge}>
                      {actionRequiredCounts.history}
                    </Box>
                  ) : null
                }
              >
                History
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="pending" pt="xl">
              <CollabsList
                collabs={pendingCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>

            <Tabs.Panel value="accepted" pt="xl">
              <CollabsList
                collabs={acceptedCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>

            <Tabs.Panel value="reviewing" pt="xl">
              <CollabsList
                collabs={reviewingCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>

            <Tabs.Panel value="history" pt="xl">
              <CollabsList
                collabs={historyCollabs}
                role={userInfo.userType}
                onReview={handleOpenReviewModal}
              />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
      {entityForReviewModal && (
        <ReviewModal
          opened={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          revieweeType={
            (entityForReviewModal as User).userType || "BRAND_USER"
          }
          revieweeName={entityForReviewModal.name || ""}
          revieweeId={
            (entityForReviewModal as Brand).id ||
            (entityForReviewModal as User).email
          }
        />
      )}
      <Modal
        opened={fundingSuccessModalOpen}
        onClose={() => setFundingSuccessModalOpen(false)}
        title={<Title order={3}>Funding Successful!</Title>}
        centered
      >
        <Stack align="center" gap="lg" p="md">
          <Text size="lg" ta="center">
            Congratulations! The collab has been successfully funded.
          </Text>

          {fundedCollabDetails && (
            <Card withBorder p="md" radius="md" w="100%">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Collab with:
                  </Text>
                  <Text fw={600}>
                    {userInfo.userType === "BRAND_USER"
                      ? fundedCollabDetails.creator?.name
                      : fundedCollabDetails.brand?.name}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Product(s):
                  </Text>
                  <Text fw={500} lineClamp={1}>
                    {fundedCollabDetails.products
                      ?.map((p) => p.name)
                      .join(", ")}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Amount Funded:
                  </Text>
                  <Text fw={600} c="secondary.6">
                    ${fundedCollabDetails.price.toLocaleString()}
                  </Text>
                </Group>
              </Stack>
            </Card>
          )}

          <Text c="dimmed" ta="center" size="sm">
            The other party has been notified and the collab is now active.
          </Text>
          <Button
            color="secondary"
            onClick={() => setFundingSuccessModalOpen(false)}
            size="md"
          >
            Awesome!
          </Button>
        </Stack>
      </Modal>
    </Box>
  );
}

export default CollabsPage;
